// Pannello di configurazione integrazione FEQ — Firma Elettronica Qualificata
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Button } from '../../../components/ui/Button'
import { CredentialsNote } from './CredentialsNote'
import api from '../../../lib/api'

const schema = z.object({
  expiryDays: z.coerce.number().int().min(1).max(30).default(7),
  sendEmailNotification: z.boolean().default(true),
  requireFiscalCode: z.boolean().default(false),
  autoRequestOnReportApproval: z.boolean().default(false),
})

type FormValues = z.infer<typeof schema>

interface FEQConfigProps {
  currentMetadata?: Record<string, unknown>
  onSaved?: () => void
}

export function FEQConfig({ currentMetadata, onSaved }: FEQConfigProps) {
  const qc = useQueryClient()

  const { register, handleSubmit, formState: { errors } } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      expiryDays: (currentMetadata?.['expiryDays'] as number) ?? 7,
      sendEmailNotification: (currentMetadata?.['sendEmailNotification'] as boolean) ?? true,
      requireFiscalCode: (currentMetadata?.['requireFiscalCode'] as boolean) ?? false,
      autoRequestOnReportApproval: (currentMetadata?.['autoRequestOnReportApproval'] as boolean) ?? false,
    },
  })

  const saveMutation = useMutation({
    mutationFn: async (data: FormValues) => {
      await api.put('/integrations/feq/config', { metadata: data })
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['integrations'] })
      onSaved?.()
    },
  })

  return (
    <div className="space-y-5">
      <CredentialsNote envVars={['FEQ_PROVIDER', 'FEQ_API_KEY', 'FEQ_API_URL']} />

      <div className="rounded-lg border border-[var(--fc-border)] p-3 bg-[var(--fc-surface)]">
        <p className="text-xs text-[var(--fc-foreground-muted)]">
          <span className="font-medium text-[var(--fc-foreground)]">Provider supportati:</span>{' '}
          Namirial (default) e InfoCert. Selezionare il provider tramite la variabile
          d'ambiente <span className="font-mono text-[var(--fc-primary)]">FEQ_PROVIDER=namirial|infocert</span>.
          Entrambi richiedono un contratto commerciale attivo con il provider.
        </p>
      </div>

      <form onSubmit={handleSubmit((data) => saveMutation.mutate(data))} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-[var(--fc-foreground)] mb-1">
            Scadenza richiesta firma (giorni)
          </label>
          <input
            type="number"
            min={1}
            max={30}
            className="w-full rounded-md border border-[var(--fc-border)] bg-[var(--fc-background)] px-3 py-2 text-sm text-[var(--fc-foreground)]"
            {...register('expiryDays')}
          />
          <p className="mt-1 text-xs text-[var(--fc-foreground-muted)]">
            Dopo quanti giorni scade il link di firma inviato al cliente.
          </p>
        </div>

        <div className="space-y-2">
          {[
            {
              key: 'sendEmailNotification',
              label: 'Invia email di notifica al firmatario',
              desc: 'Il provider invia automaticamente un\'email con il link di firma.',
            },
            {
              key: 'requireFiscalCode',
              label: 'Richiedi codice fiscale del firmatario',
              desc: 'Obbligatorio per firma qualificata con verifica identità.',
            },
            {
              key: 'autoRequestOnReportApproval',
              label: 'Richiedi firma automaticamente all\'approvazione rapportino',
              desc: 'Se abilitato, viene avviato il processo di firma al momento dell\'approvazione.',
            },
          ].map(({ key, label, desc }) => (
            <div key={key} className="flex items-start gap-2">
              <input
                type="checkbox"
                id={key}
                className="mt-0.5 h-4 w-4 rounded border-[var(--fc-border)]"
                {...register(key as keyof FormValues)}
              />
              <div>
                <label htmlFor={key} className="text-sm text-[var(--fc-foreground)]">{label}</label>
                <p className="text-xs text-[var(--fc-foreground-muted)]">{desc}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="flex justify-end gap-3 pt-2">
          <Button type="button" variant="secondary" onClick={onSaved}>
            Annulla
          </Button>
          <Button type="submit" isLoading={saveMutation.isPending}>
            Salva configurazione
          </Button>
        </div>
      </form>
    </div>
  )
}
